import { Link } from "@tanstack/react-router";
import { UsersRound } from "lucide-react";
import { useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import {
  type DirectReport,
  usePersonDirectReports,
  usePersonDottedLineReports,
} from "@/hooks/use-persons";
import { originLinkSearch } from "@/lib/detail-origin";

type ReportsTab = "direct" | "dotted";

/**
 * People connected below this person: those who report directly to them
 * (`managerId`), and those they mentor as a "dotted line" (`dottedLineId`).
 * Two inner tabs; the panel renders only when at least one list has entries,
 * so it never shows up empty. Each link carries this person as origin so the
 * report's page can breadcrumb back here.
 */
export function PersonDirectReportsSection({
  personId,
  personName,
  canViewTeam,
}: {
  personId: string;
  personName: string;
  /**
   * Whether this viewer may open the Team page for this person — they are the
   * manager, or an admin. The API enforces the same rule; this only decides
   * whether to offer a link that would 403.
   */
  canViewTeam: boolean;
}) {
  const { data: direct = [] } = usePersonDirectReports(personId);
  const { data: dotted = [] } = usePersonDottedLineReports(personId);

  // Default to whichever list has people (direct wins); the panel is hidden
  // entirely when both are empty.
  const [tab, setTab] = useState<ReportsTab>("direct");
  if (direct.length === 0 && dotted.length === 0) return null;

  const tabs: ReportsTab[] = [
    ...(direct.length > 0 ? (["direct"] as const) : []),
    ...(dotted.length > 0 ? (["dotted"] as const) : []),
  ];
  const activeTab: ReportsTab = tabs.includes(tab) ? tab : (tabs[0] ?? "direct");
  const shown = activeTab === "direct" ? direct : dotted;

  return (
    <DetailSection
      title="Direct reports"
      boldTitle
      className="gap-3 py-4"
      headerActions={
        canViewTeam && direct.length > 0 ? (
          <Link
            to="/dashboard/team/$personId"
            params={{ personId }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <UsersRound className="size-3.5" />
            View team
          </Link>
        ) : null
      }
    >
      <div className="flex gap-1 border-b">
        {tabs.map((t) => {
          const label = t === "direct" ? "Direct reports" : "Dotted lines";
          const count = t === "direct" ? direct.length : dotted.length;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                activeTab === t
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {activeTab === "direct" ? "No direct reports." : "No dotted lines."}
        </p>
      ) : (
        <ul className="space-y-2">
          {shown.map((report) => (
            <ReportRow
              key={report.id}
              report={report}
              personId={personId}
              personName={personName}
            />
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function ReportRow({
  report,
  personId,
  personName,
}: {
  report: DirectReport;
  personId: string;
  personName: string;
}) {
  return (
    <li>
      <Link
        to="/dashboard/persons/$personId"
        params={{ personId: report.id }}
        search={originLinkSearch("person", personId, personName)}
        className="flex flex-col rounded-md border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
      >
        <span className="font-medium text-foreground">{report.name}</span>
        {report.title && <span className="text-xs text-muted-foreground">{report.title}</span>}
      </Link>
    </li>
  );
}
